import { combineReducers } from 'redux';

import { ComboAction, ComboActionKeys } from '../actions/combo';
import { UiAction, UiActionKeys } from '../actions/ui';
import { ConfigsData, configsDataInitialState } from './configs';
import { getSelectedSpreads, SelectedSpreads } from '../utils/helpers';
import { ZoomType } from '../constants';

export interface ThumbnailData {
	label: string;
	position: number;
}

export interface ExternalPublicationData {
	globalId: number | null;
	spreads: number[][];
	thumbnails: ThumbnailData[];
	thumbnailPath: string;
	spreadPath?: string;
	configs?: ConfigsData;
}

interface ExternalPublicationState extends ExternalPublicationData {
	configs: ConfigsData;
	isLoading: boolean;
	itemsInRow: number | 'AUTO';
	selection: SelectedSpreads;
}

export interface ComboState {
	isCombining: boolean;
	percentage: number;
	externalPublication: ExternalPublicationState;
}

const externalPublicationInitialState: ExternalPublicationState = {
	globalId: null,
	spreads: [],
	configs: configsDataInitialState,
	thumbnails: [],
	thumbnailPath: '',
	isLoading: false,
	itemsInRow: 'AUTO',
	selection: {
		spreadIndices: [],
		lastSelectedSpreadIndex: -1,
		lastSelectedShiftSpreadIndex: -1,
	},
};

const isCombining = (state: boolean = false, action: ComboAction | UiAction): boolean => {
	switch (action.type) {
		case ComboActionKeys.CREATE_COMBO:
			return true;
		case ComboActionKeys.FINISH_COMBO:
		case UiActionKeys.RESET:
			return false;
		default:
			return state;
	}
};

const percentage = (state: number = 0, action: ComboAction | UiAction): number => {
	switch (action.type) {
		case ComboActionKeys.UPDATE_COMBO_PERCENTAGE:
			return action.percentage;
		case ComboActionKeys.FINISH_COMBO:
		case UiActionKeys.RESET:
			return 0;
		default:
			return state;
	}
};

const externalPublication = (state: ExternalPublicationState = externalPublicationInitialState, action: ComboAction | UiAction): ExternalPublicationState => {
	switch (action.type) {
		case ComboActionKeys.GET_EXTERNAL_PUBLICATION_DATA:
			return {
				...state,
				isLoading: true,
			};
		case ComboActionKeys.SET_EXTERNAL_PUBLICATION_DATA:
			return {
				...state,
				...action.data,
				isLoading: false,
			};
		case ComboActionKeys.SELECT_EXTERNAL_SPREAD: {
			const { spreadIndex, allSpreads, isCtrlPressed, isShiftPressed } = action;
			const { spreadIndices, lastSelectedSpreadIndex, lastSelectedShiftSpreadIndex } = state.selection;

			return {
				...state,
				selection: {
					...state.selection,
					...getSelectedSpreads(spreadIndex, allSpreads, spreadIndices, lastSelectedSpreadIndex, lastSelectedShiftSpreadIndex, isCtrlPressed, isShiftPressed, true),
				},
			};
		}
		case ComboActionKeys.SELECT_ALL_EXTERNAL_SPREADS:
			return {
				...state,
				selection: {
					...state.selection,
					spreadIndices: state.spreads.map((item, index) => index),
				},
			};
		case ComboActionKeys.DESELECT_ALL_EXTERNAL_SPREADS:
			return {
				...state,
				selection: {
					...state.selection,
					spreadIndices: [],
				},
			};
		case ComboActionKeys.ZOOM_EXTERNAL_SPREADS: {
			const MIN_ITEMS = 2;
			const MAX_ITEMS = 6;
			const { zoom, autoItemsInRow } = action;
			let { itemsInRow } = state;

			if (zoom === ZoomType.FIT) {
				itemsInRow = 'AUTO';
			} else {
				if (itemsInRow === 'AUTO') {
					itemsInRow = autoItemsInRow;
				}

				const step = action.zoom === ZoomType.OUT ? 1 : -1;
				itemsInRow += step;

				if (itemsInRow < MIN_ITEMS) {
					itemsInRow = MIN_ITEMS;
				}

				if (itemsInRow > MAX_ITEMS) {
					itemsInRow = MAX_ITEMS;
				}
			}

			return {
				...state,
				itemsInRow,
			};
		}
		case ComboActionKeys.RESET_EXTERNAL_PUBLICATION_DATA:
		case UiActionKeys.RESET:
			return externalPublicationInitialState;
		default:
			return state;
	}
};

export const combo = combineReducers<ComboState>({
	isCombining,
	percentage,
	externalPublication,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer/combo.ts